"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.normaliseSoundCue = normaliseSoundCue;
exports.normaliseSoundCues = normaliseSoundCues;
exports.parseTimerState = parseTimerState;
exports.serialiseTimerState = serialiseTimerState;
exports.createInitialTimerState = createInitialTimerState;
exports.hasPreCountdownConfigured = hasPreCountdownConfigured;
exports.isTimerInPreCountdown = isTimerInPreCountdown;
exports.getPreCountdownRemainingSeconds = getPreCountdownRemainingSeconds;
exports.isTimerRunning = isTimerRunning;
exports.getElapsedSeconds = getElapsedSeconds;
exports.getRemainingSeconds = getRemainingSeconds;
exports.hasReachedMax = hasReachedMax;
exports.hasMetMin = hasMetMin;
exports.getDeadlineRemainingSeconds = getDeadlineRemainingSeconds;
exports.hasReachedDeadline = hasReachedDeadline;
exports.isDeadlineTimerActive = isDeadlineTimerActive;
exports.resolveCueFireAtMs = resolveCueFireAtMs;
exports.isCueDue = isCueDue;
const DEFAULT_MODE = 'stopwatch';
const VALID_ANCHORS = ['elapsed', 'remaining', 'deadlineRemaining'];
const DEFAULT_STATE = {
    mode: DEFAULT_MODE,
    startedAt: null,
    stoppedAt: null,
    minSeconds: null,
    maxSeconds: null,
    targetAt: null,
    soundCues: [],
    preCountdownSeconds: null,
    preCountdownAudioId: null,
    armedAt: null
};
const VALID_MODES = ['blind', 'stopwatch', 'max', 'min', 'deadline'];
function isValidMode(mode) {
    return typeof mode === 'string' && VALID_MODES.includes(mode);
}
function isValidAnchor(anchor) {
    return typeof anchor === 'string' && VALID_ANCHORS.includes(anchor);
}
/**
 * Validate and normalise a single sound cue from parsed JSON or script input.
 */
function normaliseSoundCue(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const entry = raw;
    const audioId = typeof entry.audioId === 'string' ? entry.audioId.trim() : '';
    if (!audioId)
        return null;
    const atSeconds = Number(entry.atSeconds);
    if (!Number.isFinite(atSeconds) || atSeconds < 0)
        return null;
    const anchor = isValidAnchor(entry.anchor) ? entry.anchor : 'elapsed';
    return { audioId, anchor, atSeconds };
}
/**
 * Validate and filter an array of sound cues.
 */
function normaliseSoundCues(raw) {
    if (!Array.isArray(raw))
        return [];
    const cues = [];
    for (const item of raw) {
        const cue = normaliseSoundCue(item);
        if (cue)
            cues.push(cue);
    }
    return cues;
}
/**
 * Parse a variable value (string) into TimerState. Returns default state for invalid/missing input.
 */
function parseTimerState(value) {
    if (value === undefined || value === null || value === '') {
        return { ...DEFAULT_STATE, soundCues: [] };
    }
    const raw = typeof value === 'string' ? value : String(value);
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') {
            return { ...DEFAULT_STATE, soundCues: [] };
        }
        const mode = isValidMode(parsed.mode) ? parsed.mode : DEFAULT_MODE;
        const startedAt = typeof parsed.startedAt === 'number' && Number.isFinite(parsed.startedAt) ? parsed.startedAt : null;
        const stoppedAt = typeof parsed.stoppedAt === 'number' && Number.isFinite(parsed.stoppedAt) ? parsed.stoppedAt : null;
        const minSeconds = typeof parsed.minSeconds === 'number' && Number.isFinite(parsed.minSeconds) && parsed.minSeconds >= 0 ? parsed.minSeconds : null;
        const maxSeconds = typeof parsed.maxSeconds === 'number' && Number.isFinite(parsed.maxSeconds) && parsed.maxSeconds >= 0 ? parsed.maxSeconds : null;
        const targetAt = typeof parsed.targetAt === 'number' && Number.isFinite(parsed.targetAt) ? parsed.targetAt : null;
        const soundCues = normaliseSoundCues(parsed.soundCues);
        const preCountdownSeconds = typeof parsed.preCountdownSeconds === 'number' &&
            Number.isFinite(parsed.preCountdownSeconds) &&
            parsed.preCountdownSeconds > 0
            ? parsed.preCountdownSeconds
            : null;
        const preCountdownAudioId = typeof parsed.preCountdownAudioId === 'string' && parsed.preCountdownAudioId.trim().length > 0
            ? parsed.preCountdownAudioId.trim()
            : null;
        const armedAt = typeof parsed.armedAt === 'number' && Number.isFinite(parsed.armedAt) ? parsed.armedAt : null;
        return {
            mode,
            startedAt,
            stoppedAt,
            minSeconds,
            maxSeconds,
            targetAt,
            soundCues,
            preCountdownSeconds,
            preCountdownAudioId,
            armedAt
        };
    }
    catch {
        return { ...DEFAULT_STATE, soundCues: [] };
    }
}
/**
 * Serialise TimerState to JSON string for storage.
 */
function serialiseTimerState(state) {
    return JSON.stringify(state);
}
/**
 * Create initial timer state (not started). Optionally set mode.
 */
function createInitialTimerState(mode) {
    const m = mode !== undefined && isValidMode(mode) ? mode : DEFAULT_MODE;
    return {
        mode: m,
        startedAt: null,
        stoppedAt: null,
        minSeconds: m === 'min' ? null : null,
        maxSeconds: m === 'max' ? null : null,
        targetAt: m === 'deadline' ? null : null,
        soundCues: [],
        preCountdownSeconds: null,
        preCountdownAudioId: null,
        armedAt: null
    };
}
/** True when a pre-countdown duration is configured on the timer. */
function hasPreCountdownConfigured(state) {
    return state.preCountdownSeconds !== null && state.preCountdownSeconds > 0;
}
/**
 * True during the pre-countdown window after startTimer and before startedAt.
 */
function isTimerInPreCountdown(state, nowMs) {
    if (!hasPreCountdownConfigured(state) ||
        state.armedAt === null ||
        state.startedAt === null ||
        state.stoppedAt !== null) {
        return false;
    }
    return nowMs < state.startedAt;
}
/**
 * Whole seconds remaining in pre-countdown (5, 4, 3…). Returns 0 when not in pre-countdown.
 */
function getPreCountdownRemainingSeconds(state, nowMs) {
    if (!isTimerInPreCountdown(state, nowMs))
        return 0;
    return Math.max(1, Math.ceil((state.startedAt - nowMs) / 1000));
}
/**
 * Whether the timer is currently running (started and not stopped).
 */
function isTimerRunning(state) {
    return state.startedAt !== null && state.stoppedAt === null;
}
/**
 * Elapsed time in seconds. Uses stoppedAt as end if set, otherwise nowMs.
 */
function getElapsedSeconds(state, nowMs) {
    if (state.startedAt === null)
        return 0;
    const endMs = state.stoppedAt !== null ? state.stoppedAt : nowMs;
    const elapsedMs = Math.max(0, endMs - state.startedAt);
    return Math.floor(elapsedMs / 1000);
}
/**
 * Remaining seconds for max mode (countdown). Returns null for other modes.
 */
function getRemainingSeconds(state, nowMs) {
    if (state.mode !== 'max' || state.maxSeconds === null || state.maxSeconds <= 0)
        return null;
    const elapsed = getElapsedSeconds(state, nowMs);
    const remaining = Math.max(0, state.maxSeconds - elapsed);
    return remaining;
}
/**
 * True when in max mode and elapsed >= maxSeconds (time's up).
 */
function hasReachedMax(state, nowMs) {
    if (state.mode !== 'max' || state.maxSeconds === null)
        return false;
    const elapsed = getElapsedSeconds(state, nowMs);
    return elapsed >= state.maxSeconds;
}
/**
 * True when in min mode and elapsed >= minSeconds (min duration met).
 */
function hasMetMin(state, nowMs) {
    if (state.mode !== 'min' || state.minSeconds === null)
        return false;
    const elapsed = getElapsedSeconds(state, nowMs);
    return elapsed >= state.minSeconds;
}
/**
 * Remaining whole seconds until deadline targetAt. Returns null outside deadline mode.
 */
function getDeadlineRemainingSeconds(state, nowMs) {
    if (state.mode !== 'deadline' || state.targetAt === null)
        return null;
    if (state.stoppedAt !== null)
        return 0;
    return Math.max(0, Math.floor((state.targetAt - nowMs) / 1000));
}
/** True when deadline mode and now is at or past targetAt (unless already stopped). */
function hasReachedDeadline(state, nowMs) {
    if (state.mode !== 'deadline' || state.targetAt === null)
        return false;
    if (state.stoppedAt !== null)
        return true;
    return nowMs >= state.targetAt;
}
/** Deadline countdown is actively ticking (not stopped, target in the future). */
function isDeadlineTimerActive(state, nowMs) {
    if (state.mode !== 'deadline' || state.targetAt === null || state.stoppedAt !== null) {
        return false;
    }
    return nowMs < state.targetAt;
}
/**
 * Compute UTC ms when a sound cue should fire. Returns null if the anchor is incompatible
 * with the timer mode or required timestamps are missing.
 */
function resolveCueFireAtMs(state, cue) {
    if (cue.anchor === 'elapsed') {
        if (state.startedAt === null)
            return null;
        return state.startedAt + cue.atSeconds * 1000;
    }
    if (cue.anchor === 'remaining') {
        if (state.mode !== 'max' || state.maxSeconds === null || state.startedAt === null)
            return null;
        const fireAt = state.startedAt + (state.maxSeconds - cue.atSeconds) * 1000;
        if (cue.atSeconds > state.maxSeconds)
            return null;
        return fireAt;
    }
    if (cue.anchor === 'deadlineRemaining') {
        if (state.mode !== 'deadline' || state.targetAt === null)
            return null;
        return state.targetAt - cue.atSeconds * 1000;
    }
    return null;
}
/**
 * Whether a cue should have fired by nowMs. Returns false if the timer stopped before the cue was due.
 */
function isCueDue(state, cue, nowMs) {
    const fireAt = resolveCueFireAtMs(state, cue);
    if (fireAt === null)
        return false;
    if (state.stoppedAt !== null && state.stoppedAt < fireAt)
        return false;
    return nowMs >= fireAt;
}
//# sourceMappingURL=TimerState.js.map